import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { GenericIDRespopnse } from 'src/app/models/generic-id-response.model';
import { LinkUserDto, MessageDto, PSUser } from 'src/app/models/ps-user.model';
import { TrainingProgramApplicationUtils } from 'src/app/utils/training-program-application.util';
import { environment } from 'src/environments/environment';
import { UserServiceInterface } from './user.interface';
import { NotificationService } from '../notification/notification.service';
import { NotificationSearchForm } from 'src/app/models/notification-search.model';
declare function navigate(url): string;

@Injectable({ providedIn: 'root' })
export class UserService implements UserServiceInterface {
  private featureWithRolesMap = new Map([
    ['withdram-program', [TrainingProgramApplicationUtils.DesignatedSigningAuthorityId]],
    ['issue-record-of-completion', [TrainingProgramApplicationUtils.DesignatedSigningAuthorityId]]
  ]);
  private instructorFeatures = ['manage-classroom', 'issue-certificate', 'manage-enrolment', 'enrol-learner'];
  private affiliatedAdminFeatures = ['manage-classroom', 'enrol-learner', 'issue-certificate'];
  private adminFeatures = [...this.affiliatedAdminFeatures, 'create-application'];
  private dsaFeatures = ['all'];
  private asaFeatures = ['all'];

  private roleWithFeaturesMap = new Map([
    [TrainingProgramApplicationUtils.DesignatedSigningAuthorityId, [...this.dsaFeatures]],
    [TrainingProgramApplicationUtils.AdministratorId, [...this.adminFeatures]],
    [TrainingProgramApplicationUtils.AffiliateAdministratorId, [...this.affiliatedAdminFeatures]],
    [TrainingProgramApplicationUtils.InstructorId, [...this.instructorFeatures]],
    [TrainingProgramApplicationUtils.AffiliateInstructorId, [...this.instructorFeatures]],
    [TrainingProgramApplicationUtils.AlternateSigningAuthorityId, [...this.asaFeatures]]
  ]);

  private enrolmentPaths = ['/marketplace/enrolment', '/marketplace/enrolment-summary', '/training-provider/enrolment', '/fr/marketplace/enrolment',
    '/fr/marketplace/enrolment-summary', '/fr/training-provider/enrolment'];
  private classroomPaths = ['/training-provider/create-classroom', '/training-provider/edit-classroom', '/training-provider/view-classroom',
    '/fr/training-provider/create-classroom', '/fr/training-provider/edit-classroom', '/fr/training-provider/view-classroom'];
  private instructorRoutes = ['/training-provider/training-schedules', '/fr/training-provider/training-schedules', ...this.classroomPaths, ...this.enrolmentPaths];
  private affiliatedAdminRoutes = ['/training-provider/my-training-programs', '/fr/training-provider/my-training-programs', '/training-provider/teams', '/fr/training-provider/teams', ...this.instructorRoutes];
  private administratorRoutes = [...this.affiliatedAdminRoutes];

  private roleRoutesMap = new Map([
    [TrainingProgramApplicationUtils.DesignatedSigningAuthorityId, [...this.administratorRoutes, '/training-provider/withdraw-program',
      '/training-provider/program-details/withdrawn-application', '/fr/training-provider/withdraw-program', '/fr/training-provider/program-details/withdrawn-application',
      '/training-provider/my-training-programs', '/training-provider/application-for-new-training-delivery', '/fr/training-provider/my-training-programs',
      '/training-provider/my-notifications', '/fr/training-provider/my-notifications',
      '/training-provider/reporting', '/fr/training-provider/reporting',
      '/training-provider/account-settings', '/fr/training-provider/account-settings',
      '/training-provider/my-learners', '/fr/training-provider/my-learners', '/fr/training-provider/application-for-new-training-delivery']],
    [TrainingProgramApplicationUtils.AdministratorId, [...this.administratorRoutes,
      '/training-provider/my-notifications', '/fr/training-provider/my-notifications',
      '/training-provider/my-learners', '/fr/training-provider/my-learners',
      '/training-provider/reporting', '/fr/training-provider/reporting',
      '/training-provider/account-settings', '/fr/training-provider/account-settings',
      '/training-provider/application-for-new-training-delivery', '/fr/training-provider/application-for-new-training-delivery'
    ]],
    [TrainingProgramApplicationUtils.AffiliateAdministratorId, this.affiliatedAdminRoutes],
    [TrainingProgramApplicationUtils.InstructorId, this.instructorRoutes],
    [TrainingProgramApplicationUtils.AffiliateInstructorId, this.instructorRoutes],
    [TrainingProgramApplicationUtils.AlternateSigningAuthorityId, [...this.administratorRoutes]],
  ]);

  currentUser: PSUser
  unreadNotificationCount: number = -1;
  PERMISSIONS_KEY = 'permissions';
  hideFooter = false;
  hideWorkerMenu = false;
  constructor(private httpClient: HttpClient, public translateService: TranslateService, public notificationService: NotificationService) {
  }

  GetCurrentUser(reLoad: boolean = true): Observable<PSUser> {
    if (!reLoad && this.currentUser)
      return of(this.currentUser);
    else {
      let userAsObservable = this.httpClient.get<any>(environment.apiBaseUrl + `PublicSecure/GetCurrentUser`);
      userAsObservable.subscribe(x => this.currentUser = x, err => console.log('Unauthorized'));
      return userAsObservable;
    }
  }

  gotoLogout() {
    localStorage.clear();
    sessionStorage.clear();
    let gatewayPath = environment.apiBaseUrl.replace('/api/', '')

    let lang = this.translateService.currentLang == 'fr' ? 'fr' : 'en';
    navigate(gatewayPath + "/signout?lang=" + lang)
  }


  LinkPersonId(userToLink: LinkUserDto): Observable<boolean> {
    return this.httpClient.post<any>(environment.apiBaseUrl + `PublicSecure/LinkPerson`, userToLink);
  }

  CreateWorkerAccount(userToLink: LinkUserDto): Observable<GenericIDRespopnse> {
    return this.httpClient.post<any>(environment.apiBaseUrl + `PublicSecure/CreateWorkerAccount`, userToLink);
  }

  private messages: MessageDto[] = []
  getMessages(): MessageDto[] { return this.messages }
  addMessage(message: MessageDto) { this.messages.push(message) }
  addSucessMessage(messageText: string) { this.messages.push({ MessageType: 'success', MessageText: messageText }) }
  addFailureMessage(messageText: string) { this.messages.push({ MessageType: 'critical', MessageText: messageText }) }
  clearMesssages() { this.messages = [] }

  LoadUnreadNotificationCount() {
    const notificationForm = new NotificationSearchForm();
    return this.notificationService.SearchNotifications(notificationForm)
      .subscribe((data) => {
        if (!this.isUserAdminOrDSA()) {
          this.unreadNotificationCount = data
            .filter(
              (notification) =>
                (notification.IsRead == null || notification.IsRead == false) &&
                (notification.NotificationTypeId?.toLowerCase() !=
                  TrainingProgramApplicationUtils.MinistryNotification.toLowerCase() &&
                  notification.NotificationTypeId?.toLowerCase() !=
                  TrainingProgramApplicationUtils.MyTeamNotification.toLowerCase())
            )
            .length;
        } else {
          this.unreadNotificationCount = data.filter(
            (notification) =>
              notification.IsRead == null || notification.IsRead == false
          ).length;
        }
      });
  }

  isUserAdminOrDSA(): boolean {
    if (!this.currentUser) {
      this.httpClient.get<any>(environment.apiBaseUrl + `PublicSecure/GetCurrentUser`).subscribe((user) => {
        this.currentUser = user;
        return this.checkIfIsUserAdminOrDSA();
      });
    }
    else {
      return this.checkIfIsUserAdminOrDSA();
    }
    return false;
  }

  checkIfIsUserAdminOrDSA(): boolean {
    if (this.currentUser.Roles && this.currentUser.Roles.length > 0) {
      return this.currentUser.Roles.some((x) => {
        const roleId = x.PersonRoleId ? x.PersonRoleId?.toUpperCase() : null;
        return (
          roleId == TrainingProgramApplicationUtils.AdministratorId ||
          roleId == TrainingProgramApplicationUtils.DesignatedSigningAuthorityId ||
          roleId == TrainingProgramApplicationUtils.AlternateSigningAuthorityId
        );
      });
    }
    return false;
  }

  getCurrentUserRoles(): Observable<string[]> {
    return this.GetCurrentUser().pipe(map((user) => {
      if (!user?.Roles?.length) return []
      const roles = user.Roles.map(x => x.PersonRoleId || '');
      return roles;
    }))
  }

  checkUserPermissionForFeature(featureName: string): Observable<boolean> {
    const roleIds = this.featureWithRolesMap.get(featureName);
    return this.getCurrentUserRoles().pipe(map((roles: string[]) => {
      return roles.some(x => roleIds?.includes(x))
    }));
  }

  getAuthorizedFeaturesForCurrentUser() {
    return this.getCurrentUserRoles().pipe(map((roles) => {
      const allFeatures: string[] = []
      roles.forEach((roleId) => {
        const roleFeatures = this.roleWithFeaturesMap.get(roleId.toUpperCase());
        if (!roleFeatures) return;
        roleFeatures.forEach((feature) => {
          if (!feature) return;
          if (!allFeatures.includes(feature))
            allFeatures.push(feature)
        })
      })
      return allFeatures;
    }))
  }

  getAuthorizedRoutesForCurrentUser(): Observable<string[]> {
    return this.getCurrentUserRoles().pipe(map((roles) => {
      const allRoutes: string[] = []
      roles.forEach((roleId) => {
        const roleRoutes = this.roleRoutesMap.get(roleId.toUpperCase());
        if (!roleRoutes) return;
        roleRoutes.forEach((route) => {
          if (!allRoutes.includes(route))
            allRoutes.push(route)
        })
      })
      return allRoutes;
    }))
  }
}
